import { useMemo } from 'react';
import _map from 'lodash/map';
import _isEmpty from 'lodash/isEmpty';
import _keyBy from 'lodash/keyBy';
import _flatMap from 'lodash/flatMap';
import { useConfigService } from '@thd-nucleus/experience-context';
import {
  EXPERIENCE_TAGS as PROMO_EXPERIENCE_TAGS,
  FORWARDS_BOGO_SUBEXPERIENCE_TAGS,
  BACKWARDS_BOGO_SUBEXPERIENCE_TAGS
} from '@thd-olt-component-react/promotion-products/dist/utils/constants';
import { useProductDataStore, usePromoStore } from '../store';

const defaultData = {
  bogoItemInCart: null,
  currentPromoTier: 0,
  isPromoAchieved: false,
  progressMade: 0,
  promoCartItemsObj: {},
  promoCartQuantity: 0,
  promoCartValue: 0,
  promoSuccessLevel: '',
  secondListItemInCart: false
};

const triggerDefaultData = () => {
  if (typeof window !== 'undefined') {
    window.LIFE_CYCLE_EVENT_BUS
      .trigger('promotion-products.custom-promo-cart', defaultData);
  }
};

export default (itemIds) => {
  let promoPop;
  let totalPromo = [];

  const productData = useProductDataStore((state) => state.productData);
  const updatePromoTotal = usePromoStore((state) => state.updatePromoTotal);
  const addPromoItem = usePromoStore((state) => state.addPromoItem);
  const fsPropBogoEnabled = useConfigService(
    'fs:custom-module-layout-designer--bogo'
  );
  const fsPropReverseBogoEnabled = useConfigService(
    'fs:custom-module-layout-designer--reverse-bogo'
  );

  useMemo(() => {
    let previousPromoType;
    let minPurchaseAmount;
    const promoItems = [];
    const bogoCompanions = [];

    if (!_isEmpty(itemIds)) {
      _map(itemIds, (item, index) => {
        const conditionalPromotions = productData?.[item?.itemId]?.pricing?.conditionalPromotions;
        const promotion = productData?.[item?.itemId]?.pricing?.promotion;

        if (_isEmpty(conditionalPromotions) || !conditionalPromotions[0].experienceTag) return null;
        const minPurchaseQuantity = conditionalPromotions?.[0]?.eligibilityCriteria?.[0]?.minPurchaseQuantity;
        const tiers = conditionalPromotions?.[0]?.reward?.tiers;
        const tierPrices = tiers?.map((tier) => tier.minPurchaseAmount);
        const rewardAmountPerOrder = tiers?.map((tier) => tier.rewardAmountPerOrder);
        const newItem = { ...item, type: promotion?.experienceTag };

        if (index + 1 === itemIds.length) {
          if (!previousPromoType) {
            previousPromoType = promotion?.experienceTag;
          } else if (previousPromoType !== promotion?.experienceTag) {
            previousPromoType = promotion?.experienceTag;
            promoPop = false;
          } else if (previousPromoType === promotion?.experienceTag) {
            previousPromoType = promotion?.experienceTag;
            promoPop = true;
          }
        }

        const imgUrl = productData[item.itemId]?.media?.images[0]?.url
          .replace(/<SIZE>/, '145');

        const duplicate = promoItems?.[item?.itemId];

        if (conditionalPromotions) {
          if (duplicate) {
            duplicate.quantity += 1;
          }

          if (promotion?.experienceTag !== PROMO_EXPERIENCE_TAGS.BOGO) {
            promoItems.push({
              type: promotion?.experienceTag,
              itemId: item.itemId,
              price: item.price,
              quantity: item.quantity || 1,
              image: String(imgUrl),
              minPurchaseQuantity,
              tierPrices,
              rewardAmountPerOrder
            });

            return newItem;
          }

          if (promotion?.experienceTag === PROMO_EXPERIENCE_TAGS.BOGO
            && (fsPropBogoEnabled && fsPropReverseBogoEnabled)
          ) {

            if (bogoCompanions.includes(String(item.itemId))) {
              const bogoObj = conditionalPromotions.find(
                (promo) => promo.subExperienceTag === FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne);
              const reverseBogoObj = conditionalPromotions.find(
                (promo) => promo.subExperienceTag === BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne);
              const testBogo = bogoObj?.eligibilityCriteria[0].omsSkus.includes(String(item.itemId));
              const testReverseBogo = reverseBogoObj?.eligibilityCriteria[1].omsSkus.includes(String(item.itemId));

              if (
                promotion?.subExperienceTag === BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne
                && testReverseBogo
              ) {
                const bogoSources = reverseBogoObj?.eligibilityCriteria[0].omsSkus;
                const bogoSource = itemIds.find(({ itemId }) => bogoSources.indexOf(String(itemId)) !== -1);
                if (!bogoSource) {
                  return newItem;
                }
                promoItems.push({
                  bogoCompanion: {
                    ...productData[item.itemId],
                    media: {
                      image: {
                        url: productData[item.itemId].media.images[0].url
                      }
                    }
                  },
                  type: `${FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne}-${bogoSource.itemId}`,
                  itemId: item.itemId,
                  price: item.price,
                  quantity: item.quantity || 1,
                  image: imgUrl,
                  minPurchaseQuantity,
                  tierPrices,
                  rewardAmountPerOrder
                });
                return newItem;
              }
              if (
                promotion?.subExperienceTag === FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne
                && testBogo
              ) {
                const bogoTargets = bogoObj?.eligibilityCriteria[1].omsSkus;
                const bogoTarget = itemIds.find(({ itemId }) => bogoTargets.indexOf(String(itemId)) !== -1);
                if (!bogoTarget) {
                  return newItem;
                }
                promoItems.push({
                  bogoCompanion: {
                    ...productData[item.itemId],
                    media: {
                      image: {
                        url: productData[item.itemId].media.images[0].url
                      }
                    }
                  },
                  type: `${BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne}-${bogoTarget.itemId}`,
                  itemId: item.itemId,
                  price: item.price,
                  quantity: item.quantity || 1,
                  image: imgUrl,
                  minPurchaseQuantity,
                  tierPrices,
                  rewardAmountPerOrder
                });
                return newItem;
              }
            }

            if (promotion?.subExperienceTag === FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne) {
              bogoCompanions.push(...conditionalPromotions?.[0]?.eligibilityCriteria?.[1]?.omsSkus);
              promoItems.push({
                type: `${promotion?.subExperienceTag}-${item.itemId}`,
                itemId: item.itemId,
                price: item.price,
                quantity: item.quantity || 1,
                image: imgUrl,
                minPurchaseQuantity,
                tierPrices,
                rewardAmountPerOrder
              });
              return newItem;
            }

            if (promotion?.subExperienceTag === BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne) {
              bogoCompanions.push(...conditionalPromotions?.[0]?.eligibilityCriteria?.[0]?.omsSkus);
              promoItems.push({
                type: `${promotion?.subExperienceTag}-${item.itemId}`,
                itemId: item.itemId,
                price: item.price,
                quantity: item.quantity || 1,
                image: imgUrl,
                minPurchaseQuantity,
                tierPrices,
                rewardAmountPerOrder
              });

              return newItem;
            }

          }
        }

        return newItem;
      });

      const data = _map(promoItems, (promoItem) => {
        const promoItemType = promoItem.type ? promoItem.type.split('-')[0] : null;
        const promoItemTypeSku = promoItem.type ? promoItem.type.split('-')[1] : null;

        const getAllPrices = _map(promoItems, (item) => {
          const total = item.quantity * item.price;
          return promoItem.type === item.type ? total : 0;
        });
        const total = getAllPrices.reduce((partialSum, price) => partialSum + price, 0);
        minPurchaseAmount = (_isEmpty(promoItem.tierPrices) || promoItem.tierPrices[0] === null)
          ? false
          : promoItem.tierPrices?.find(
            (tier) => {
              return tier >= total || tier[promoItem.tierPrices.length];
            });

        const promoTier = promoItem.tierPrices?.findIndex((index) => index === minPurchaseAmount);
        const currentPromoTier = promoTier === -1 ? promoItem.tierPrices.length - 1 : promoTier;
        const divide = minPurchaseAmount ? (total / minPurchaseAmount) : total;
        const percent = (Number.isNaN(divide * 100) ? 100 : divide * 100);
        const filteredItems = promoItems.filter((newItem) => promoItem.type === newItem.type);
        const filteredQty = filteredItems.map((itm) => itm.quantity);
        const totalQty = filteredQty.reduce((partialSum, qty) => partialSum + qty, 0);
        const remainingItems = () => {
          if (promoItem.type === PROMO_EXPERIENCE_TAGS.BMSM) {
            return totalQty;
          }

          if (promoItem.type === PROMO_EXPERIENCE_TAGS.MSB) {
            return promoItem?.minPurchaseQuantity - totalQty < 0
              ? 0
              : promoItem?.minPurchaseQuantity - totalQty;
          }

          if (
            promoItemType === `${FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne}-${promoItem.itemId}`
            || promoItemType === `${BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne}-${promoItem.itemId}`
          ) {
            const maxPurchaseQuantity = -2;
            return totalQty + maxPurchaseQuantity;
          }

          return 1;
        };

        const progressMade = () => {
          if (promoItem?.minPurchaseQuantity) {
            if (promoItem.minPurchaseQuantity === remainingItems()) {
              return 0;
            }
            const minQty = ((promoItem?.minPurchaseQuantity - remainingItems()) / promoItem?.minPurchaseQuantity) * 100;
            return minQty <= 100 ? minQty : 100;
          }
          return percent <= 100 ? percent : 100;
        };

        const isPromoAchieved = () => {
          if (!remainingItems() || (!promoItem?.minPurchaseQuantity && percent >= 100)) {
            return true;
          }

          return false;
        };

        const hydratedData = {
          anchorItemId: (
            promoItemType === FORWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne
            || promoItemType === BACKWARDS_BOGO_SUBEXPERIENCE_TAGS.buyOneGetOne
          )
            ? promoItemTypeSku
            : null,
          bogoSelectedProduct: promoItem?.bogoCompanion || null,
          bogoItemInCart: null,
          currentPromoTier: currentPromoTier || 0,
          isPromoAchieved: isPromoAchieved(),
          progressMade: progressMade(),
          promoCartItemsObj: filteredItems,
          promoCartQuantity: totalQty,
          promoCartValue: total,
          promoSuccessLevel: remainingItems() <= 0 ? 'Success' : '',
          secondListItemInCart: false
        };
        const tier = currentPromoTier === promoItem.rewardAmountPerOrder?.length - 1 && !minPurchaseAmount
          ? currentPromoTier
          : currentPromoTier - 1;

        const checkTotal = totalPromo.find((promoPrice) => {
          return promoPrice === promoItem.rewardAmountPerOrder?.[tier];
        });
        if ((!checkTotal || _isEmpty(totalPromo)) && promoItem.rewardAmountPerOrder?.[tier]) {
          if (promoPop) {
            totalPromo.pop();
          }
          totalPromo.push(promoItem.rewardAmountPerOrder?.[tier]);
          updatePromoTotal(totalPromo.reduce((partialSum, price) => partialSum + price, 0));
        } else if (checkTotal && _isEmpty(totalPromo)) {
          updatePromoTotal(totalPromo.pop().reduce((partialSum, price) => partialSum + price, 0));
        } else if (checkTotal && !_isEmpty(totalPromo)) {
          if (totalPromo.slice(-1)[0] !== checkTotal) {
            totalPromo.pop();
          }
          updatePromoTotal(totalPromo.reduce((partialSum, price) => partialSum + price, 0));
        } else if (!checkTotal && !_isEmpty(totalPromo)) {
          totalPromo.pop();
          updatePromoTotal(totalPromo.reduce((partialSum, price) => partialSum + price, 0));
        } else if (!checkTotal && _isEmpty(totalPromo)) {
          totalPromo.pop();
          updatePromoTotal(totalPromo.reduce((partialSum, price) => partialSum + price, 0));
        }

        const promoSourcesAndTargets = _flatMap(
          productData[promoItem.itemId].pricing.conditionalPromotions?.[0]?.eligibilityCriteria,
          (criteria) => criteria.omsSkus
        );

        const groupKey = [promoItem.type, ...promoSourcesAndTargets].join('-');

        return {
          groupKey,
          itemId: String(promoItem.itemId),
          data: hydratedData,
        };
      });

      if (_isEmpty(data)) {
        triggerDefaultData();
        updatePromoTotal(0);
        return addPromoItem([]);
      }

      const promoDataByItemId = _keyBy(data, 'itemId');
      return addPromoItem(promoDataByItemId);
    }

    triggerDefaultData();
    updatePromoTotal(0);
    return addPromoItem([]);
  }, [itemIds]);
};
